// Lightweight client-side persistence for the Document Studio.
//
// Model: the studio is always scoped to a FILE (a document or template).
// Each file owns one or more CONVERSATIONS (threads). Everything is keyed by
// fileId so reopening a file continues where the user left off.
//
// Two localStorage areas:
//  1. `regoStudioFiles`            — persisted file state (survives across tabs/sessions)
//  2. `regoStudioHandoff:{fileId}` — a one-shot payload written by the launcher
//                                    button just before opening the studio tab.
//
// This is intentionally storage-based (not URL params) so we can pass a full
// document/template between tabs without hitting URL length limits. Swap these
// helpers for real API calls when the backend is ready.

const FILES_KEY = "regoStudioFiles";
const handoffKey = (fileId) => `regoStudioHandoff:${fileId}`;

const safeParse = (raw, fallback) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
};

/* ----------------------------- Handoff (launcher -> studio) ---------------- */

// Called by the launcher button. `context` describes the file to edit:
// { fileId, title, docType, document?, status?, version?, sourceUrl? }
export const writeHandoff = (context) => {
  if (!context?.fileId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(handoffKey(context.fileId), JSON.stringify(context));
  } catch (e) {
    /* storage unavailable */
  }
};

// Read handoff without clearing (safe for React StrictMode double-render).
export const peekHandoff = (fileId) => {
  if (!fileId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(handoffKey(fileId));
    return raw ? safeParse(raw, null) : null;
  } catch (e) {
    return null;
  }
};

// Read + clear the handoff for a file (one-shot).
export const consumeHandoff = (fileId) => {
  if (!fileId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(handoffKey(fileId));
    if (!raw) return null;
    window.localStorage.removeItem(handoffKey(fileId));
    return safeParse(raw, null);
  } catch (e) {
    return null;
  }
};

/* ----------------------------- File state persistence --------------------- */

const loadAll = () => {
  if (typeof window === "undefined") return {};
  return safeParse(window.localStorage.getItem(FILES_KEY), {});
};

const saveAll = (all) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FILES_KEY, JSON.stringify(all));
  } catch (e) {
    /* storage unavailable */
  }
};

export const loadFile = (fileId) => (fileId ? loadAll()[fileId] || null : null);

export const saveFile = (fileState) => {
  if (!fileState?.fileId) return;
  const all = loadAll();
  all[fileState.fileId] = fileState;
  saveAll(all);
};
